import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTracker } from './TrackerContext';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface StudentCardPos { x: number; y: number }

interface Hovered { studentId: string; studentName: string; pos: StudentCardPos }

interface ReassignTarget {
  studentId: string; studentName: string;
  currentMentorId: string; currentMentorName: string;
}

interface StudentCardContextValue {
  // hover card
  hovered: Hovered | null;
  onStudentEnter: (studentId: string, studentName: string, e: React.MouseEvent) => void;
  onStudentMove:  (e: React.MouseEvent) => void;
  onStudentLeave: () => void;
  onCardEnter:    () => void;
  onCardLeave:    () => void;
  // detail modal
  detailStudentId: string | null;
  openDetail:  (studentId: string) => void;
  closeDetail: () => void;
  // reassign modal
  reassignTarget: ReassignTarget | null;
  openReassign:  (t: ReassignTarget) => void;
  closeReassign: () => void;
  // a student deactivated or reassigned from the popup — see useOnStudentChange
  studentChange: { studentId: string; at: number } | null;
  notifyStudentChange: (studentId: string) => void;
  // session helpers
  adminPassword: string;
  canSeeDetail: boolean;  // admin or coordinator
}

const StudentCardContext = createContext<StudentCardContextValue | null>(null);

export function useStudentCard() {
  const ctx = useContext(StudentCardContext);
  if (!ctx) throw new Error('useStudentCard must be used inside StudentCardProvider');
  return ctx;
}

// Pages listing students reload when one is deactivated or reassigned from
// the student popup, so the student doesn't linger as Active
export function useOnStudentChange(reload: () => void) {
  const { studentChange } = useStudentCard();
  const latest = useRef(reload);
  latest.current = reload;
  useEffect(() => { if (studentChange) latest.current(); }, [studentChange]);
}

// ── Provider ──────────────────────────────────────────────────────────────────

export const StudentCardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session } = useTracker();

  const adminPassword = session?.kind === 'admin'
    ? session.adminPassword
    : session?.kind === 'coordinator'
    ? `COORD:${session.mentorId}`
    : '';

  const canSeeDetail = session?.kind === 'admin' || session?.kind === 'coordinator';

  // ── hover state ──────────────────────────────────────────────────────────
  const [hovered, setHovered] = useState<Hovered | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  };

  const onStudentEnter = useCallback((studentId: string, studentName: string, e: React.MouseEvent) => {
    clearTimer();
    const pos = { x: e.clientX, y: e.clientY };
    timerRef.current = setTimeout(() => setHovered({ studentId, studentName, pos }), 260);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onStudentMove = useCallback((e: React.MouseEvent) => {
    setHovered(prev => prev ? { ...prev, pos: { x: e.clientX, y: e.clientY } } : null);
  }, []);

  const onStudentLeave = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => setHovered(null), 120);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCardEnter = useCallback(() => { clearTimer(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const onCardLeave = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => setHovered(null), 120);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => clearTimer(), []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── detail state ─────────────────────────────────────────────────────────
  const [detailStudentId, setDetailStudentId] = useState<string | null>(null);
  const openDetail  = useCallback((id: string) => { clearTimer(); setHovered(null); setDetailStudentId(id); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const closeDetail = useCallback(() => setDetailStudentId(null), []);

  // ── reassign state ────────────────────────────────────────────────────────
  const [reassignTarget, setReassignTarget] = useState<ReassignTarget | null>(null);
  const openReassign  = useCallback((t: ReassignTarget) => { clearTimer(); setHovered(null); setReassignTarget(t); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const closeReassign = useCallback(() => setReassignTarget(null), []);

  // ── student changed (deactivated / reassigned) ───────────────────────────
  const [studentChange, setStudentChange] = useState<{ studentId: string; at: number } | null>(null);
  const notifyStudentChange = useCallback((studentId: string) => setStudentChange({ studentId, at: Date.now() }), []);

  return (
    <StudentCardContext.Provider value={{
      hovered, onStudentEnter, onStudentMove, onStudentLeave, onCardEnter, onCardLeave,
      detailStudentId, openDetail, closeDetail,
      reassignTarget, openReassign, closeReassign,
      studentChange, notifyStudentChange,
      adminPassword, canSeeDetail,
    }}>
      {children}
    </StudentCardContext.Provider>
  );
};
